import { useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/*
 * View state that lives in the address bar.
 *
 * A tab, a filter, a selected row: leave the page and come back and it should
 * be as you left it, and a link you send someone should show them what you
 * were looking at. Keeping that state in `useState` alone loses both.
 *
 * Two rules hold this together and both matter:
 *
 * 1. Writes MERGE into whatever is already in the query string. Several pieces
 *    of state share one URL, and a writer that rebuilt the string from scratch
 *    would drop the others' parameters — which is exactly what the older
 *    hand-rolled list pages do to each other.
 * 2. A value equal to its default is REMOVED, not written. Otherwise every
 *    link carries a dozen parameters saying "everything is normal".
 *
 * Navigation always replaces rather than pushes: typing in a filter box should
 * not fill the back button with keystrokes.
 *
 * Writes are also BATCHED to the end of the tick. React Router hands each
 * writer the parameters as they stood at render time, so two pieces of state
 * set in one event handler — selecting an entity sets the selection, the ego
 * and the panel — would each compute from the string before the others wrote,
 * and the last one would win. Collecting them and applying them once keeps
 * every write.
 */

/** How one kind of value survives the round trip through a string */
export interface UrlCodec<T> {
  /** Turns the raw parameter into a value, or null when it is not a valid one */
  read(raw: string): T | null;
  /** Turns a value into a parameter; null or '' leaves the parameter out */
  write(value: T): string | null;
}

export const asString: UrlCodec<string> = {
  read: (raw) => raw,
  write: (value) => value || null,
};

/** One of a known set — anything else is treated as absent, not as an error */
export const asOneOf = <T extends string>(allowed: readonly T[]): UrlCodec<T> => ({
  read: (raw) => (allowed.includes(raw as T) ? (raw as T) : null),
  write: (value) => (value ? String(value) : null),
});

export const asNumber = (min = Number.MIN_SAFE_INTEGER, max = Number.MAX_SAFE_INTEGER): UrlCodec<number> => ({
  read: (raw) => {
    const value = Number(raw);
    return Number.isFinite(value) && value >= min && value <= max ? value : null;
  },
  write: (value) => (Number.isFinite(value) ? String(value) : null),
});

/** Present means true; `?labels` and `?labels=1` both read as true */
export const asFlag: UrlCodec<boolean> = {
  read: (raw) => raw !== '0' && raw !== 'false',
  write: (value) => (value ? '1' : null),
};

/**
 * A flag that starts on, so the URL only ever says when it is off. `asFlag`
 * can only say "on", which is no use to a rail or a toggle that opens by
 * default.
 */
export const asFlagOn: UrlCodec<boolean> = {
  read: (raw) => raw !== '0' && raw !== 'false',
  write: (value) => (value ? null : '0'),
};

/** A set of values in one parameter, comma separated, order kept */
export const asList = <T extends string>(allowed?: readonly T[]): UrlCodec<T[]> => ({
  read: (raw) => {
    const parts = raw
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean) as T[];
    const kept = allowed ? parts.filter((part) => allowed.includes(part)) : parts;
    return kept.length ? kept : null;
  },
  write: (value) => (value.length ? value.join(',') : null),
});

/** The same, as a Set, for state held that way (expanded rows, chosen ids) */
export const asSet = <T extends string>(allowed?: readonly T[]): UrlCodec<Set<T>> => {
  const list = asList<T>(allowed);
  return {
    read: (raw) => {
      const parts = list.read(raw);
      return parts ? new Set(parts) : null;
    },
    write: (value) => list.write(Array.from(value)),
  };
};

type Writer = (
  update: (current: URLSearchParams) => URLSearchParams,
  options: { replace: boolean },
) => void;

/** Changes made in this tick, waiting to be applied together */
let pending: Record<string, string | null> | null = null;

function queue(write: Writer, changes: Record<string, string | null>): void {
  const first = pending === null;
  pending = { ...(pending ?? {}), ...changes };
  if (!first) return;
  void Promise.resolve().then(() => {
    const all = pending ?? {};
    pending = null;
    write((current) => {
      const merged = new URLSearchParams(current);
      for (const [key, value] of Object.entries(all)) {
        if (value === null || value === '') merged.delete(key);
        else merged.set(key, value);
      }
      return merged;
    }, { replace: true });
  });
}

const same = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (a instanceof Set && b instanceof Set) {
    return a.size === b.size && Array.from(a).every((item) => b.has(item));
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, index) => item === b[index]);
  }
  return false;
};

/**
 * One piece of view state, kept in the query string.
 *
 * Reads like `useState`: `const [tab, setTab] = useUrlState('tab', 'overview',
 * asOneOf(TABS))`. The default never appears in the URL, and an unreadable
 * parameter falls back to it rather than throwing.
 */
export function useUrlState<T>(
  key: string,
  fallback: T,
  codec: UrlCodec<T> = asString as unknown as UrlCodec<T>,
): [T, (next: T) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get(key);

  // The codec, the default and the router's writer are read through refs so
  // that `set` keeps one identity for the life of the component, the way
  // `useState`'s setter does. Callers put it in dependency arrays, and a
  // setter that changed every render would churn everything downstream of it.
  const codecRef = useRef(codec);
  codecRef.current = codec;
  const fallbackRef = useRef(fallback);
  fallbackRef.current = fallback;
  const writeRef = useRef(setParams);
  writeRef.current = setParams;

  const value = useMemo(() => {
    if (raw === null) return fallback;
    const parsed = codecRef.current.read(raw);
    return parsed === null ? fallback : parsed;
  }, [raw, fallback]);

  const set = useCallback((next: T) => {
    const written = same(next, fallbackRef.current) ? null : codecRef.current.write(next);
    queue(writeRef.current, { [key]: written });
  }, [key]);

  return [value, set];
}

/**
 * Several parameters in one go, for an interaction that changes more than one
 * thing — choosing a filter usually means going back to page one, and that
 * should be a single change, not two.
 *
 * `null` removes a parameter.
 */
export function useUrlWriter(): (changes: Record<string, string | null>) => void {
  const [, setParams] = useSearchParams();
  const writeRef = useRef(setParams);
  writeRef.current = setParams;
  return useCallback((changes) => queue(writeRef.current, changes), []);
}
